import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../features/auth/use-auth.js';
import { Card } from '../components/ui/card.js';
import {
  HOTELS_QUERY_KEY,
  listGlobalCalendar,
  listHotels,
  type CalendarEntry,
  type Hotel,
} from '../features/hotels/api.js';
import { authFetch } from '../features/auth/auth-fetch.js';
import { getFavoriteHotels } from '../features/dashboard/api.js';
import { getContentSummary, listContent } from '../features/content/api.js';
import {
  PLANS_ORG_QUERY_KEY,
  listAllPlans,
  type MonthlyPlanListItem,
  type MonthlyPlanStatus,
} from '../features/plans/api.js';
import { listApprovalBatches } from '../features/approvals/api.js';
import { batchesAwaitingDecision } from '../features/approvals/batch-activity.js';
import { PLAN_STATUS_ORDER as PLAN_STATUS_ORDER_KEYS } from '../features/plans/plan-status.js';
import { PlanStatusGrid } from '../features/plans/plan-status-grid.js';
import { getBudgetStatus, type BudgetStatus } from '../features/usage/budget-api.js';
import { Sparkline } from '../components/visual/sparkline.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { buildAttentionItems } from '../features/dashboard/attention-model.js';
import { AttentionPanel } from '../features/dashboard/attention-panel.js';
import type { ApprovalBatch } from '../features/approvals/types.js';

/** Hotel thumbs shown in the dashboard hero strip. */
const HERO_SLOTS = 6;

/**
 * Plans as the dashboard reads them — `MonthlyPlanListItem` is the canonical
 * shape the `/plans` endpoint returns (it carries `slotCounts` and
 * `topicTitles` when the server could compute them, optional on the client).
 * The dashboard's previous local `PlanRow` was a subset that swallowed non-ok
 * responses: sharing `['plans','org']` with the Tervek page means a non-ok
 * response must surface as an error everywhere, so this alias is the throwing
 * one. Local mutations (archive/unarchive/delete) refresh the cache via
 * `invalidateQueries({ queryKey: ['plans'] })`, which hits this entry.
 */
type PlanRow = MonthlyPlanListItem;

/**
 * The post fields the dashboard panels read. Structurally satisfied by the
 * `ContentRow` the /content endpoints return, so the panels stay independent of
 * which endpoint fed them.
 */
interface PostRow {
  id: string;
  monthlyPlanId: string;
  hotelId: string;
  scheduledFor: string;
  status: string;
}

interface UsageDto {
  totalCostUsd: number;
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCachedTokens: number;
  cacheHitRate: number;
}

const HU_MONTHS = [
  'Január',
  'Február',
  'Március',
  'Április',
  'Május',
  'Június',
  'Július',
  'Augusztus',
  'Szeptember',
  'Október',
  'November',
  'December',
];

const POST_STATUS_LABEL: Record<string, string> = {
  draft_topic: 'Téma piszkozat',
  topic_approved: 'Téma jóváhagyva',
  draft_content: 'Tartalom piszkozat',
  content_pending: 'Tartalom jóváhagyásra',
  content_approved: 'Jóváhagyott',
  scheduled: 'Ütemezve',
  published: 'Publikálva',
  archived: 'Archivált',
};

/**
 * How many posts the activity feed reads.
 *
 * It previously received every post in the org, assembled from one
 * `/plans/:id/posts` request per monthly plan — the single biggest source of
 * request amplification in the app. The feed renders eight entries drawn from the
 * most recent activity, so a bounded newest-first slice is the same output for a
 * fixed cost. Well clear of `ContentQuerySchema`'s `limit.max(200)`, so raising
 * it stays a one-line change rather than a runtime 400.
 *
 * The Attention panel deliberately fetches no posts at all: it needs *counts*,
 * and counting a truncated page understated them. It reads exact per-hotel
 * tallies from GET /content/summary — see attention-model.ts.
 */
const ACTIVITY_POST_LIMIT = 50;

/** Rows the Attention panel shows before it starts reporting a remainder. */
const ATTENTION_ITEM_LIMIT = 5;

/** Upcoming-post cut-off: yesterday, matching what the strip has always shown. */
function upcomingCutoffIso(now: Date): string {
  return new Date(now.getTime() - 24 * 3600 * 1000).toISOString();
}

/**
 * Throws on a failed response instead of returning null.
 *
 * Returning null made a failure indistinguishable from a slow load: the card
 * rendered "—" with the hint stuck on "betöltés…" forever, so an admin looking
 * at AI spend saw a value that never arrived and no indication anything was
 * wrong. Same treatment `listAllPlans` got.
 */
async function fetchUsageThisMonth(): Promise<UsageDto> {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
  const r = await authFetch(
    `/admin/usage?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
  if (!r.ok) throw new Error('Nem sikerült betölteni az AI-használatot.');
  return r.json() as Promise<UsageDto>;
}

function DashboardPage() {
  const { user } = useAuth();

  // Use the same shared keys the rest of the app reads — see HOTELS_QUERY_KEY
  // and PLANS_ORG_QUERY_KEY in the feature api modules. Sharing the key turns
  // what used to be three separate /hotels (or /plans) round-trips on a single
  // visit into one, and using the throwing `listAllPlans` here (instead of a
  // local silent fallback) keeps the cache's error semantics the same for
  // every reader: a /plans failure renders the error banner on the Tervek page
  // instead of an empty list.
  const hotelsQ = useQuery<Hotel[]>({ queryKey: HOTELS_QUERY_KEY, queryFn: listHotels });
  const plansQ = useQuery({
    queryKey: PLANS_ORG_QUERY_KEY,
    queryFn: listAllPlans,
    enabled: !!hotelsQ.data,
  });
  // Org-wide tallies + the upcoming strip: one request, independent of how many
  // hotels or plans exist.
  const summaryQ = useQuery({
    queryKey: ['content-summary'],
    queryFn: () =>
      getContentSummary({ upcomingFrom: upcomingCutoffIso(new Date()), upcomingLimit: 6 }),
  });
  // Newest-first slice for the activity feed — see ACTIVITY_POST_LIMIT.
  const activityPostsQ = useQuery({
    queryKey: ['dash-activity-posts'],
    queryFn: () => listContent({ sort: 'newest', limit: ACTIVITY_POST_LIMIT }),
  });
  const usageQ = useQuery({
    queryKey: ['dash-usage'],
    queryFn: fetchUsageThisMonth,
    enabled: user?.role === 'admin',
  });
  const budgetQ = useQuery<BudgetStatus>({
    queryKey: ['ai-budget-status'],
    queryFn: getBudgetStatus,
  });

  // One org-wide list instead of one request per plan. Same query key as the
  // sidebar badge and the approvals page, so this is usually already cached.
  const batchesQ = useQuery<ApprovalBatch[]>({
    queryKey: ['approval-batches', 'org'],
    queryFn: () => listApprovalBatches({}),
    staleTime: 30_000,
  });
  const batches = batchesQ.data ?? [];

  const hotelMap = useMemo(() => {
    const m = new Map<string, Hotel>();
    for (const h of hotelsQ.data ?? []) m.set(h.id, h);
    return m;
  }, [hotelsQ.data]);

  const planMap = useMemo(() => {
    const m = new Map<string, PlanRow>();
    for (const p of plansQ.data ?? []) m.set(p.id, p);
    return m;
  }, [plansQ.data]);

  const now = new Date();
  const inDays = (d: Date) => Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  // Already filtered and ordered by the API (soonest first, from the cut-off) —
  // nothing left to do client-side.
  const upcomingPosts = summaryQ.data?.upcoming ?? [];

  const postStats = useMemo(
    () => ({
      total: summaryQ.data?.total ?? 0,
      byStatus: (summaryQ.data?.byStatus ?? {}) as Record<string, number>,
    }),
    [summaryQ.data],
  );

  const activeBatches = useMemo(() => batchesAwaitingDecision(batches, 5), [batches]);

  // Exact per-hotel counts straight from SQL, ordered most-imminent-first. No
  // post list is fetched for this: counting a page of posts is what silently
  // understated the numbers, and paging them newest-first meant the posts inside
  // the window were the ones scheduled furthest out.
  const attention = useMemo(
    () =>
      buildAttentionItems(
        {
          tallies: summaryQ.data?.byHotelStatus ?? [],
          batches,
          hotels: hotelMap,
          plans: planMap,
          now: Date.now(),
        },
        ATTENTION_ITEM_LIMIT,
      ),
    [summaryQ.data, batches, hotelMap, planMap],
  );

  const greeting = greetingByHour();
  const upcomingCount = upcomingPosts.length;
  const favoritesQ = useQuery<string[]>({
    queryKey: ['dashboard-favorites'],
    queryFn: getFavoriteHotels,
  });

  // Hero strip = favorites first, then other active hotels, both sorted
  // alphabetically (HU locale). HERO_SLOTS total. Favorites are unlimited,
  // so the pinned set itself must be trimmed too: the first HERO_SLOTS by
  // name win.
  const heroHotels = useMemo(() => {
    const all = hotelsQ.data ?? [];
    const favSet = new Set(favoritesQ.data ?? []);

    // Inactive hotels stay out of the strip even when pinned — same rule as
    // the filler below; with unlimited favorites a few deactivated pins
    // would otherwise crowd out every live hotel.
    const pinned = all
      .filter((h) => favSet.has(h.id) && h.isActive)
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'))
      .slice(0, HERO_SLOTS);
    const rest = all
      .filter((h) => !favSet.has(h.id) && h.isActive)
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'));

    const slotsLeft = Math.max(0, HERO_SLOTS - pinned.length);
    return [...pinned, ...rest.slice(0, slotsLeft)];
  }, [hotelsQ.data, favoritesQ.data]);

  return (
    <div className="space-y-6">
      {/* HERO BAND — teal gradient, greeting, hotel image strip */}
      <div className="h2-hero">
        {/* Aurora layer — three slowly drifting color blobs behind the
            noise overlay. Pure CSS, GPU-accelerated, respects
            prefers-reduced-motion. Sits at z-index:0 below the content. */}
        <div className="h2-hero-aurora" aria-hidden="true">
          <span className="h2-hero-aurora__blob h2-hero-aurora__blob--a" />
          <span className="h2-hero-aurora__blob h2-hero-aurora__blob--b" />
          <span className="h2-hero-aurora__blob h2-hero-aurora__blob--c" />
        </div>
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <h1 className="h2-hero-greeting">
              {greeting},{' '}
              <em>{user?.firstName ?? user?.name ?? user?.email?.split('@')[0] ?? ''}</em>.
            </h1>
            <div
              className="mt-2 inline-flex items-center gap-2 text-body"
              style={{ color: 'rgba(236,249,240,0.7)' }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 4,
                  background: 'var(--accent-highlight)',
                  boxShadow: '0 0 10px var(--accent-highlight)',
                  display: 'inline-block',
                }}
              />
              {upcomingCount > 0
                ? `${upcomingCount} közelgő poszt vár ma rád`
                : 'Nincs ütemezett poszt — minden tiszta.'}
            </div>
          </div>
        </div>

        {/* Bottom row: hotel strip + date */}
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          {heroHotels.length > 0 ? (
            <div className="h2-hero-strip">
              {heroHotels.map((h) => (
                <Link
                  key={h.id}
                  to="/app/hotels/$hotelId"
                  params={{ hotelId: h.id }}
                  className="h2-hero-thumb"
                  data-label={h.name}
                  style={{
                    backgroundImage: h.coverImageUrl ? `url('${h.coverImageUrl}')` : undefined,
                  }}
                  aria-label={h.name}
                />
              ))}
            </div>
          ) : (
            <div />
          )}
          <div
            className="ml-auto whitespace-nowrap text-right text-h2 leading-tight tracking-tight"
            style={{ color: 'rgba(236,249,240,0.55)', fontFamily: 'Fraunces, serif' }}
          >
            {new Date().toLocaleDateString('hu-HU', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              weekday: 'long',
            })}
          </div>
        </div>
      </div>

      <UpcomingDatesTimeline />

      {/* KPI ROW — bigger visual cards with sparklines */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <BigKpi
          label="Szállodák"
          value={hotelsQ.data?.length ?? '—'}
          hint={hotelsQ.data?.length === 1 ? 'aktív' : 'aktív összesen'}
          to="/app/hotels"
          tone="info"
        />
        <BigKpi
          label="Havi tervek"
          value={plansQ.data?.length ?? '—'}
          hint={`${plansQ.data?.filter((p) => p.status !== 'archived').length ?? 0} aktív`}
          to="/app/plans"
          tone="success"
          spark={planStatusSpark(plansQ.data ?? [])}
        />
        <BigKpi
          label="Posztok"
          value={postStats.total || '—'}
          hint={`${postStats.byStatus['content_approved'] ?? 0} kész`}
          to="/app/content"
          tone="success"
          spark={postStatsSpark(postStats.byStatus)}
        />
        {user?.role === 'admin' && (
          <BigKpi
            label="AI hívások (hónap)"
            value={usageQ.isError ? '!' : (usageQ.data?.totalCalls ?? '—')}
            hint={
              budgetQ.data?.blocked
                ? `Keret elfogyott — $${budgetQ.data.monthlySpendUsd.toFixed(2)} / $${budgetQ.data.monthlyBudgetUsd?.toFixed(2)}`
                : budgetQ.data?.monthlyBudgetUsd != null &&
                    budgetQ.data.percentUsed != null &&
                    budgetQ.data.percentUsed >= 0.8
                  ? `${Math.round(budgetQ.data.percentUsed * 100)}% felhasználva ($${budgetQ.data.monthlySpendUsd.toFixed(2)} / $${budgetQ.data.monthlyBudgetUsd.toFixed(2)})`
                  : usageQ.isError
                    ? 'nem tölthető be'
                    : usageQ.data
                      ? `$${usageQ.data.totalCostUsd.toFixed(4)} · ${Math.round((usageQ.data.cacheHitRate ?? 0) * 100)}% cache`
                      : 'betöltés…'
            }
            to="/app/admin/usage"
            tone="ai"
            {...(usageQ.data?.totalCalls && usageQ.data.totalCalls > 0
              ? { spark: [1, 2, 2, 3, 4, 5, 5, 6, 7, 8, 9, 10, usageQ.data.totalCalls] }
              : {})}
          />
        )}
      </div>

      {/* Figyelmedet igényli + Aktivitás — két új panel a közelgő/jóváhagyási
          sor előtt, egyenlő széles oszlopokban. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <AttentionPanel attention={attention} />
        <ActivityFeed
          posts={activityPostsQ.data ?? []}
          plans={plansQ.data ?? []}
          hotels={hotelsQ.data ?? []}
          batches={batches}
          hotelMap={hotelMap}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Közelgő posztok</h2>
            <Link
              to="/app/plans"
              className="text-meta font-medium text-[var(--brand-700)] hover:underline"
            >
              Naptár →
            </Link>
          </div>
          {upcomingPosts.length === 0 ? (
            <EmptyState
              icon="calendar"
              title="Nincs ütemezett poszt"
              body="Hozz létre egy havi tervet és indíts AI témajavaslatot."
              cta={{ label: 'Új terv', to: '/app/plans' }}
            />
          ) : (
            <ul className="space-y-1">
              {upcomingPosts.map((p) => {
                const hotel = hotelMap.get(p.hotelId);
                const plan = planMap.get(p.monthlyPlanId);
                const d = new Date(p.scheduledFor);
                const days = inDays(d);
                const canDeepLink = Boolean(plan && hotel);
                return (
                  <li key={p.id}>
                    <Link
                      to={canDeepLink ? '/app/plans/$hotelId/$year/$month' : '/app/plans'}
                      {...(canDeepLink && plan && hotel
                        ? {
                            params: {
                              hotelId: hotel.id,
                              year: String(plan.year),
                              month: String(plan.month),
                            },
                            search: { post: p.id },
                          }
                        : {})}
                      className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-[var(--app-hover)]"
                      title="Poszt megnyitása szerkesztőben"
                    >
                      <DateBadge date={d} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-body font-medium text-[var(--ink-700)]">
                          {hotel?.name ?? 'Ismeretlen hotel'}
                        </div>
                        <div className="truncate text-meta text-[var(--fg-2)]">
                          {POST_STATUS_LABEL[p.status] ?? p.status}
                          {' · '}
                          {p.publishToFb && p.publishToIg ? 'FB+IG' : p.publishToFb ? 'FB' : 'IG'}
                          {' · '}
                          {p.brandOrSales === 'brand' ? 'Brand' : 'Sales'}
                        </div>
                      </div>
                      <div
                        className={`shrink-0 text-meta font-medium ${
                          days <= 2
                            ? 'text-[var(--danger)]'
                            : days <= 7
                              ? 'text-[var(--brand-700)]'
                              : 'text-[var(--fg-faint)]'
                        }`}
                      >
                        {days === 0 ? 'Ma' : days === 1 ? 'Holnap' : `${days} nap`}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Aktív jóváhagyások</h2>
            {activeBatches.length > 0 && (
              <span className="inline-flex items-center rounded-full bg-[var(--brand-100)] px-2 py-0.5 text-meta font-semibold uppercase tracking-wider text-[var(--brand-800)]">
                {activeBatches.length}
              </span>
            )}
          </div>
          {activeBatches.length === 0 ? (
            <EmptyState
              icon="check"
              title="Minden rendben"
              body="Nincs nyitott jóváhagyási csomag."
            />
          ) : (
            <ul className="space-y-2">
              {activeBatches.map((b) => {
                const plan = planMap.get(b.monthlyPlanId);
                const hotel = plan ? hotelMap.get(plan.hotelId) : null;
                const sentLabel = new Date(b.createdAt).toLocaleDateString('hu-HU');
                const lastView = b.lastViewedAt
                  ? new Date(b.lastViewedAt).toLocaleDateString('hu-HU')
                  : null;
                return (
                  <li key={b.id}>
                    <Link
                      to="/app/approvals/$batchId/preview"
                      params={{ batchId: b.id }}
                      className="block rounded-md border border-[var(--app-divider)] bg-[var(--app-bg)] p-3 transition-colors hover:border-[var(--brand-500)] hover:bg-[var(--app-hover)]"
                      title="Jóváhagyási előnézet megnyitása"
                    >
                      <div className="truncate text-body font-medium text-[var(--ink-700)]">
                        {hotel?.name ?? 'Ismeretlen'}
                      </div>
                      <div className="mt-0.5 text-meta text-[var(--fg-2)]">
                        {plan
                          ? `${plan.year} ${HU_MONTHS[plan.month - 1] ?? plan.month + '.'}`
                          : '—'}
                        {' · '}
                        {b.stage === 'topics' ? 'Témák' : 'Tartalom'}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-meta">
                        <span title="Csomag létrehozva és kiküldve">Kiküldve {sentLabel}</span>
                        {lastView ? (
                          <span title={`Ügyfél utoljára megnyitotta · ${b.viewCount} megtekintés`}>
                            Megnyitva {lastView}
                          </span>
                        ) : (
                          <span className="italic" title="Az ügyfél még nem nyitotta meg">
                            Még nem nyitotta meg
                          </span>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {plansQ.data && plansQ.data.length > 0 && <PlanStatusGrid plans={plansQ.data} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PLAN STATUS GRID — clickable cards with per-status counts + extras  */
/* ------------------------------------------------------------------ */

function greetingByHour(): string {
  const h = new Date().getHours();
  if (h < 4) return 'Késő van';
  if (h < 11) return 'Jó reggelt';
  if (h < 17) return 'Jó napot';
  if (h < 22) return 'Jó estét';
  return 'Késő van';
}

function planStatusSpark(plans: { status: MonthlyPlanStatus }[]): number[] {
  // Deterministic mini-chart: cumulative count in lifecycle order. Reads the
  // shared order rather than a second hand-written list — the copy that used to
  // live here listed three statuses the API cannot produce, so the spark was
  // flat for every plan in flight.
  const counts = PLAN_STATUS_ORDER_KEYS.map((s) => plans.filter((p) => p.status === s).length);
  let acc = 0;
  return counts.map((c) => (acc += c));
}

function postStatsSpark(byStatus: Record<string, number>): number[] {
  const order = [
    'draft_topic',
    'topic_pending_client',
    'topic_approved',
    'content_review',
    'content_approved',
    'published',
  ];
  let acc = 0;
  return order.map((k) => (acc += byStatus[k] ?? 0));
}

function BigKpi({
  label,
  value,
  hint,
  to,
  tone = 'info',
  spark,
}: {
  label: string;
  value: number | string;
  hint?: string;
  to?: string;
  tone?: 'info' | 'success' | 'warn' | 'ai';
  spark?: number[];
}) {
  const colors: Record<string, string> = {
    info: 'var(--info, var(--brand-500))',
    warn: 'var(--warning)',
    success: 'var(--brand-500)',
    ai: 'var(--ai-fg)',
  };
  const inner = (
    <div className="h2-kpi">
      <div className="h2-kpi-label">{label}</div>
      <div className="h2-kpi-value mt-2.5">{value}</div>
      {hint && <div className="mt-2 text-meta text-[var(--fg-2)]">{hint}</div>}
      {spark && spark.length >= 2 && (
        <Sparkline points={spark} {...(colors[tone] ? { color: colors[tone]! } : {})} />
      )}
    </div>
  );
  return to ? (
    <Link to={to} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

function DateBadge({ date }: { date: Date }) {
  return (
    <div className="flex h-11 w-11 flex-shrink-0 flex-col items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-bg)]">
      <div className="text-meta font-semibold uppercase tracking-wider text-[var(--fg-faint)]">
        {HU_MONTHS[date.getMonth()]?.slice(0, 3) ?? date.getMonth() + 1}
      </div>
      <div className="text-body font-semibold leading-none text-[var(--ink-700)]">
        {date.getDate()}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ACTIVITY FEED                                                       */
/* ------------------------------------------------------------------ */

interface ActivityEntry {
  id: string;
  kind: 'plan' | 'post' | 'hotel' | 'batch';
  actor: string;
  verb: string;
  subject: string;
  context: string;
  at: string;
}

function ActivityFeed({
  posts,
  plans,
  hotels,
  batches,
  hotelMap,
}: {
  posts: PostRow[];
  plans: PlanRow[];
  hotels: Hotel[];
  batches: ApprovalBatch[];
  hotelMap: Map<string, Hotel>;
}) {
  const entries = useMemo(() => {
    const out: ActivityEntry[] = [];
    // Recently updated posts by status — surfaces who touched what
    for (const p of posts) {
      const hotel = hotelMap.get(p.hotelId);
      if (!hotel) continue;
      const verb = postVerb(p.status);
      if (!verb) continue;
      out.push({
        id: `post-${p.id}`,
        kind: 'post',
        actor: 'AI',
        verb: verb.verb,
        subject: verb.subject,
        context: hotel.name,
        at: p.scheduledFor,
      });
    }
    // Recent plans
    for (const plan of plans) {
      const hotel = hotelMap.get(plan.hotelId);
      if (!hotel) continue;
      out.push({
        id: `plan-${plan.id}`,
        kind: 'plan',
        actor: 'Manager',
        verb: 'létrehozott egy tervet',
        subject: `${HU_MONTHS[plan.month - 1] ?? plan.month}`,
        context: hotel.name,
        at: plan.updatedAt,
      });
    }
    // Recent hotels
    for (const h of hotels) {
      out.push({
        id: `hotel-${h.id}`,
        kind: 'hotel',
        actor: 'Manager',
        verb: 'új hotelt vett fel',
        subject: h.name,
        context: '',
        at: h.createdAt,
      });
    }
    // Approval batches
    for (const b of batches) {
      out.push({
        id: `batch-${b.id}`,
        kind: 'batch',
        actor: 'Manager',
        verb: 'küldött',
        subject: `${b.postCount} posztot ügyfélnek`,
        context: b.clientEmail,
        at: b.createdAt,
      });
    }
    return out.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 8);
  }, [posts, plans, hotels, batches, hotelMap]);

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Aktivitás</h2>
        <span className="inline-flex items-center gap-1.5 text-meta text-[var(--fg-faint)]">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--brand-500)]" />
          élő
        </span>
      </div>
      {entries.length === 0 ? (
        <EmptyState
          icon="check"
          title="Még csendes"
          body="Itt fog megjelenni a csapat aktivitása."
        />
      ) : (
        <ul className="space-y-3">
          {entries.map((e) => (
            <li key={e.id} className="flex items-start gap-2.5">
              <ActivityAvatar kind={e.kind} actor={e.actor} />
              <div className="min-w-0 flex-1">
                <div className="text-meta leading-snug text-[var(--ink-700)]">
                  <span className="font-semibold">{e.actor}</span>{' '}
                  <span className="text-[var(--fg-2)]">{e.verb}</span>
                  {e.subject && <span> {e.subject}</span>}
                </div>
                <div className="text-meta text-[var(--fg-faint)]">
                  {e.context && <span>{e.context} · </span>}
                  {relativeTime(e.at)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function postVerb(status: string): { verb: string; subject: string } | null {
  switch (status) {
    case 'topic_pending_client':
      return { verb: 'kiküldte témajóváhagyásra', subject: 'a posztot' };
    case 'topic_approved':
      return { verb: 'jóváhagyott egy témát', subject: '' };
    case 'content_pending_client':
      return { verb: 'kiküldte tartalom-jóváhagyásra', subject: 'a posztot' };
    case 'content_approved':
      return { verb: 'jóváhagyta a tartalmat', subject: '' };
    case 'published':
      return { verb: 'publikálta', subject: 'a posztot' };
    case 'topic_changes_requested':
      return { verb: 'módosítást kért a témán', subject: '' };
    case 'content_changes_requested':
      return { verb: 'módosítást kért a poszton', subject: '' };
    case 'topic_rejected':
      return { verb: 'elutasította', subject: 'a témát' };
    case 'content_rejected':
      return { verb: 'elutasította', subject: 'a posztot' };
    case 'scheduled':
      return { verb: 'időzítette', subject: 'a posztot' };
    case 'waiting_info':
      return { verb: 'további információra vár', subject: 'a poszttal' };
    default:
      return null;
  }
}

function ActivityAvatar({ kind, actor }: { kind: ActivityEntry['kind']; actor: string }) {
  if (actor === 'AI') {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--ai-bg)] text-[var(--ai-fg)]">
        <svg width={11} height={11} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2 14 9l7 2-7 2-2 7-2-7-7-2 7-2z" />
        </svg>
      </span>
    );
  }
  const initials = actor
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const tone =
    kind === 'plan'
      ? 'bg-[var(--brand-100)] text-[var(--brand-800)]'
      : kind === 'hotel'
        ? 'bg-[var(--st-rejected-bg)] text-[var(--st-rejected-fg)]'
        : kind === 'batch'
          ? 'bg-[var(--st-content-review-bg)] text-[var(--st-content-review-fg)]'
          : 'bg-[var(--app-divider)] text-[var(--ink-700)]';
  return (
    <span
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-meta font-semibold ${tone}`}
    >
      {initials || '·'}
    </span>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'most';
  if (min < 60) return `${min} perce`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} órája`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} napja`;
  return new Date(iso).toLocaleDateString('hu-HU');
}

/* ------------------------------------------------------------------ */
/* UPCOMING DATES TIMELINE — next 31 days strip                        */
/* ------------------------------------------------------------------ */

const ENTRY_STYLE: Record<string, { bg: string; fg: string; dot: string; label: string }> = {
  holiday: {
    bg: 'var(--cal-holiday-bg)',
    fg: 'var(--cal-holiday-fg)',
    dot: 'var(--cal-holiday-dot)',
    label: 'Ünnep',
  },
  long_weekend: {
    bg: 'var(--cal-long-weekend-bg)',
    fg: 'var(--cal-long-weekend-fg)',
    dot: 'var(--cal-long-weekend-dot)',
    label: 'Hosszú hétvége',
  },
  commercial: {
    bg: 'var(--cal-commercial-bg)',
    fg: 'var(--cal-commercial-fg)',
    dot: 'var(--cal-commercial-dot)',
    label: 'Kereskedelmi',
  },
  observance: {
    bg: 'var(--cal-observance-bg)',
    fg: 'var(--cal-observance-fg)',
    dot: 'var(--cal-observance-dot)',
    label: 'Jeles nap',
  },
  season: {
    bg: 'var(--cal-season-bg)',
    fg: 'var(--cal-season-fg)',
    dot: 'var(--cal-season-dot)',
    label: 'Évszak',
  },
  working_saturday: {
    bg: 'var(--cal-working-saturday-bg)',
    fg: 'var(--cal-working-saturday-fg)',
    dot: 'var(--cal-working-saturday-dot)',
    label: 'Ledolgozós szombat',
  },
};

function entryStyle(kind: string) {
  return ENTRY_STYLE[kind] ?? ENTRY_STYLE.observance!;
}

const HU_MONTH_SHORT = [
  'jan',
  'feb',
  'márc',
  'ápr',
  'máj',
  'jún',
  'júl',
  'aug',
  'szept',
  'okt',
  'nov',
  'dec',
];

function isoFromDate(d: Date): string {
  // Local calendar date — toISOString() would shift by the local UTC offset
  // (e.g. CEST midnight → previous-day Z), which throws off day-to-event
  // matching by one cell.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function UpcomingDatesTimeline() {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const end = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 30); // 31 days inclusive
    return d;
  }, [today]);

  const calendar = useQuery<CalendarEntry[]>({
    queryKey: ['dash-global-calendar', isoFromDate(today), isoFromDate(end)],
    queryFn: () => listGlobalCalendar(isoFromDate(today), isoFromDate(end)),
  });

  // Expand multi-day events (long_weekend has endsOn) into per-day hits
  // for the strip. Each entry is registered on every day in [startsOn,
  // endsOn] inclusive, so the colour fills the whole interval — but the
  // anchor entry itself stays unique for the highlight list below.
  const entriesByDay = useMemo(() => {
    const m = new Map<string, CalendarEntry[]>();
    const push = (iso: string, entry: CalendarEntry) => {
      const arr = m.get(iso) ?? [];
      // Don't double-record the same entry on a day (paranoid guard).
      if (!arr.some((e) => e.id === entry.id)) arr.push(entry);
      m.set(iso, arr);
    };
    for (const e of calendar.data ?? []) {
      push(e.startsOn, e);
      if (e.endsOn && e.endsOn > e.startsOn) {
        const cursor = new Date(`${e.startsOn}T00:00:00Z`);
        const end = new Date(`${e.endsOn}T00:00:00Z`);
        cursor.setUTCDate(cursor.getUTCDate() + 1);
        while (cursor <= end) {
          push(cursor.toISOString().slice(0, 10), e);
          cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
      }
    }
    return m;
  }, [calendar.data]);

  // 31 day cells
  const days = useMemo(() => {
    const out: { iso: string; date: Date }[] = [];
    for (let i = 0; i < 31; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      out.push({ iso: isoFromDate(d), date: d });
    }
    return out;
  }, [today]);

  // List of highlighted upcoming events. Each calendar row appears once
  // — multi-day entries (long_weekend) keep their anchor + endsOn for an
  // interval-style label below.
  const highlights = useMemo(() => {
    const fromIso = isoFromDate(today);
    const toIso = isoFromDate(end);
    // Defensive client-side window filter: only entries whose interval
    // actually overlaps [today, today+30]. Belt-and-suspenders for stale
    // cache / older API responses that returned the whole year.
    return (calendar.data ?? [])
      .filter((e) => {
        const start = e.startsOn;
        const finish = e.endsOn ?? e.startsOn;
        return start <= toIso && finish >= fromIso;
      })
      .slice(0, 8);
  }, [calendar.data, today, end]);

  // Month boundary markers across the 31-day window. Group consecutive
  // days that share the same year-month and remember where the run starts
  // so we can render a month label above the right span of cells.
  const monthSegments = useMemo(() => {
    const segments: { startCol: number; span: number; label: string }[] = [];
    let i = 0;
    while (i < days.length) {
      const d = days[i]!.date;
      const ym = `${d.getFullYear()}-${d.getMonth()}`;
      let j = i + 1;
      while (j < days.length) {
        const dj = days[j]!.date;
        if (`${dj.getFullYear()}-${dj.getMonth()}` !== ym) break;
        j++;
      }
      segments.push({
        startCol: i + 1, // CSS grid is 1-indexed
        span: j - i,
        label:
          `${HU_MONTH_SHORT[d.getMonth()]} ${d.getFullYear() !== today.getFullYear() ? d.getFullYear() : ''}`.trim(),
      });
      i = j;
    }
    return segments;
  }, [days, today]);

  // Hungarian weekday short label per cell (H/K/Sze/Cs/P/Szo/V).
  // Built from getDay() (Sun=0..Sat=6) using a 7-element table.
  const HU_DOW_SHORT: Record<number, string> = {
    0: 'V',
    1: 'H',
    2: 'K',
    3: 'Sze',
    4: 'Cs',
    5: 'P',
    6: 'Szo',
  };

  return (
    <Card className="p-5">
      {/* Month strip — labels span the days that belong to each month. */}
      <div
        className="mb-1 grid gap-1"
        style={{ gridTemplateColumns: 'repeat(31, minmax(0, 1fr))' }}
      >
        {monthSegments.map((seg) => (
          <div
            key={`${seg.startCol}-${seg.label}`}
            className="border-b border-[var(--app-divider)] pb-1 text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]"
            style={{ gridColumn: `${seg.startCol} / span ${seg.span}` }}
          >
            {seg.label}
          </div>
        ))}
      </div>

      {/* Weekday row — H / K / Sze / Cs / P / Szo / V above each cell. */}
      <div
        className="mt-2 grid gap-1"
        style={{ gridTemplateColumns: 'repeat(31, minmax(0, 1fr))' }}
      >
        {days.map(({ iso, date }) => {
          const dow = date.getDay();
          const isWeekend = dow === 0 || dow === 6;
          return (
            <div
              key={`dow-${iso}`}
              className={`text-center text-meta font-medium ${
                isWeekend ? 'text-[var(--brand-700)]' : 'text-[var(--fg-faint)]'
              }`}
            >
              {HU_DOW_SHORT[dow]}
            </div>
          );
        })}
      </div>

      {/* Day strip — each day is a single square cell with a thin border,
          tinted by the highest-priority event of that day. Today wins
          over any event tint and uses the brand-green fill. */}
      <div
        className="mt-1 grid gap-1"
        style={{ gridTemplateColumns: 'repeat(31, minmax(0, 1fr))' }}
      >
        {days.map(({ iso, date }) => {
          const entries = entriesByDay.get(iso) ?? [];
          // Pick up to two distinct event KINDS for the diagonal split.
          // A day with holiday + long_weekend shows both colours; a day
          // with two same-kind rows (rare) collapses to one.
          const distinctKinds: string[] = [];
          for (const e of entries) {
            if (!distinctKinds.includes(e.kind)) distinctKinds.push(e.kind);
            if (distinctKinds.length === 2) break;
          }
          const isToday = iso === isoFromDate(today);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;

          // Cell styling priority: today > 2-kind diagonal > 1-kind tint
          // > weekend > neutral.
          let background: string;
          let borderColor: string;
          let textColor: string;
          if (isToday) {
            background = 'var(--brand-500)';
            borderColor = 'var(--brand-600)';
            textColor = '#fff';
          } else if (distinctKinds.length === 2) {
            const a = entryStyle(distinctKinds[0]!);
            const b = entryStyle(distinctKinds[1]!);
            // Diagonal split — top-left half = first kind, bottom-right
            // = second kind. Hard 50% stop so the seam is crisp at the
            // anti-diagonal (135deg = from top-left to bottom-right).
            background = `linear-gradient(135deg, ${a.bg} 0%, ${a.bg} 50%, ${b.bg} 50%, ${b.bg} 100%)`;
            borderColor = a.dot;
            textColor = a.fg;
          } else if (distinctKinds.length === 1) {
            const s = entryStyle(distinctKinds[0]!);
            background = s.bg;
            borderColor = s.dot;
            textColor = s.fg;
          } else {
            background = isWeekend ? 'var(--app-bg)' : 'var(--app-surface)';
            borderColor = 'var(--app-divider)';
            textColor = 'var(--fg-2)';
          }

          return (
            <div
              key={iso}
              className="flex aspect-square items-center justify-center rounded-md border text-meta font-semibold tabular-nums transition-colors"
              style={{ background, borderColor, color: textColor }}
              title={
                entries.length > 0
                  ? entries.map((e) => `${e.startsOn} — ${e.title}`).join('\n')
                  : `${date.toLocaleDateString('hu-HU', { weekday: 'long' })}, ${date.getDate()}.`
              }
            >
              {date.getDate()}
            </div>
          );
        })}
      </div>

      {/* Highlight list */}
      {highlights.length > 0 ? (
        <ul className="mt-6 grid gap-1.5 sm:grid-cols-2">
          {highlights.map((e) => {
            const style = entryStyle(e.kind);
            const start = new Date(`${e.startsOn}T12:00:00Z`);
            const end =
              e.endsOn && e.endsOn > e.startsOn ? new Date(`${e.endsOn}T12:00:00Z`) : null;
            const startLabel = `${HU_MONTH_SHORT[start.getUTCMonth()]} ${start.getUTCDate()}.`;
            const endLabel = end
              ? `${HU_MONTH_SHORT[end.getUTCMonth()]} ${end.getUTCDate()}.`
              : null;
            return (
              <li
                key={e.id}
                className="flex items-center gap-2 rounded-md border border-[var(--app-divider)] bg-[var(--app-bg)] px-3 py-1.5"
              >
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ background: style.dot }}
                />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-meta font-medium text-[var(--ink-700)]">
                    {e.title}
                  </div>
                  <div className="font-mono text-meta tabular-nums text-[var(--fg-faint)]">
                    {endLabel ? `${startLabel} – ${endLabel}` : startLabel}
                  </div>
                </div>
                <span
                  className="inline-flex items-center rounded-pill px-1.5 py-0.5 text-meta font-semibold uppercase tracking-wider"
                  style={{ background: style.bg, color: style.fg }}
                >
                  {style.label}
                </span>
              </li>
            );
          })}
        </ul>
      ) : calendar.isLoading ? null : (
        <p className="mt-8 text-meta italic text-[var(--fg-faint)]">
          Nincs jelölt esemény a következő 31 napban.
        </p>
      )}
    </Card>
  );
}

export const Route = createFileRoute('/app/')({
  component: DashboardPage,
});
